import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  changeClassificationLookupStatus,
  createClassificationLookup,
  deleteClassificationLookup,
  getClassificationLookups,
  reorderClassificationLookups,
  updateClassificationLookup,
} from '../api/classificationLookupApi'
import { CLASSIFICATION_LOOKUP_CONFIG } from '../config'
import type {
  ClassificationLookup,
  ClassificationLookupKind,
  ClassificationLookupPage,
  ClassificationLookupStatus,
  ClassificationLookupUpsert,
} from '../types'

/**
 * Görev formlarının seçenek sorgusu (`useActionLevels` → `['action-levels', dil]`). Stratejik hedef ve
 * katkı seçenekleri oradan gelir ve 5 dk taze sayılır; bu ekranda yapılan değişiklik (yeni kayıt, ad,
 * durum, silme, sıra) aynı oturumdaki görev formlarına beklemeden yansısın diye her başarılı yazımda
 * bu önek geçersiz kılınır. Dil parçası verilmez → iki dilin önbelleği birden tazelenir.
 */
const TASK_FORM_OPTIONS_KEY = ['action-levels'] as const

/**
 * Stratejik Hedefler / Katkılar yönetimi sunucu-durumu (127): liste + oluştur/güncelle/durum/sil/sırala
 * mutation'ları. `useEventTypes` deseniyle aynı: her başarıda liste invalidate edilir, güncel kayıt
 * cache'e yazılır (bayat modDate → 409 önlenir), sıralama iyimser uygulanıp hatada geri alınır.
 * Önbellek anahtarı türe göre ayrışır — iki ekranın listesi birbirine karışmaz.
 */
export function useClassificationLookups(kind: ClassificationLookupKind) {
  const { i18n } = useTranslation()
  const qc = useQueryClient()
  const root = CLASSIFICATION_LOOKUP_CONFIG[kind].queryKey
  const listKey = [root, i18n.language]

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: [root] })
  }

  const invalidateTaskFormOptions = () => {
    qc.invalidateQueries({ queryKey: TASK_FORM_OPTIONS_KEY })
  }

  const afterWrite = () => {
    invalidate()
    invalidateTaskFormOptions()
  }

  const applyUpdated = (updated: ClassificationLookup) =>
    qc.setQueryData<ClassificationLookupPage>(listKey, (prev) =>
      prev
        ? { ...prev, content: prev.content.map((r) => (r.id === updated.id ? updated : r)) }
        : prev,
    )

  const list = useQuery({
    queryKey: listKey,
    queryFn: () => getClassificationLookups(kind),
  })

  // meta.handledCodes: domaine özel iş kodu ekranda ele alınır → merkezi hata toast'ı (019) susar.
  const create = useMutation({
    mutationFn: (body: ClassificationLookupUpsert) => createClassificationLookup(kind, body),
    onSuccess: afterWrite,
    meta: { handledCodes: ['DUPLICATE_NAME'] },
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: ClassificationLookupUpsert }) =>
      updateClassificationLookup(kind, id, body),
    onSuccess: (data) => {
      applyUpdated(data)
      afterWrite()
    },
    meta: { handledCodes: ['DUPLICATE_NAME'] },
  })

  const changeStatus = useMutation({
    mutationFn: ({ id, body }: { id: number; body: ClassificationLookupStatus }) =>
      changeClassificationLookupStatus(kind, id, body),
    onSuccess: (data) => {
      applyUpdated(data)
      afterWrite()
    },
  })

  const remove = useMutation({
    mutationFn: (id: number) => deleteClassificationLookup(kind, id),
    onSuccess: afterWrite,
  })

  // Yeniden sıralama: liste anında (optimistic) yeni sıraya geçer; backend 1..N yazar. Hata → geri al.
  const reorder = useMutation({
    mutationFn: (orderedIds: number[]) => reorderClassificationLookups(kind, orderedIds),
    onMutate: async (orderedIds) => {
      await qc.cancelQueries({ queryKey: listKey })
      const prev = qc.getQueryData<ClassificationLookupPage>(listKey)
      if (prev) {
        const byId = new Map(prev.content.map((r) => [r.id, r]))
        const content = orderedIds
          .map((id) => byId.get(id))
          .filter((r): r is ClassificationLookup => r != null)
        qc.setQueryData<ClassificationLookupPage>(listKey, { ...prev, content })
      }
      return { prev }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(listKey, ctx.prev)
    },
    // Görev formlarındaki seçenek sırası SORT_INDEX'i izler → yalnız başarıda tazelenir.
    onSuccess: invalidateTaskFormOptions,
    onSettled: invalidate,
  })

  return { list, create, update, changeStatus, remove, reorder }
}
